import React from 'react';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';

export interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles: string[];
  /** Extra paths that should keep this item highlighted (redirects, sub-tabs). */
  matches?: string[];
}

export interface NavSection {
  heading: string;
  items: NavItem[];
}

const ADMIN = 'Admin';
const USER_MANAGER = 'UserManager';

export const NAV_SECTIONS: NavSection[] = [
  {
    heading: 'Overview',
    items: [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: <SpaceDashboardOutlinedIcon />,
        roles: [ADMIN],
      },
    ],
  },
  {
    heading: 'Licensing',
    items: [
      // Users leads the group: licences are looked up by account, so this is
      // where an operator starts.
      {
        label: 'Users',
        path: '/users',
        icon: <GroupOutlinedIcon />,
        roles: [ADMIN, USER_MANAGER],
      },
      {
        label: 'Đăng ký',
        path: '/signup-settings',
        icon: <HowToRegOutlinedIcon />,
        roles: [ADMIN, USER_MANAGER],
      },
      {
        label: 'Licenses',
        path: '/licenses/michanger',
        icon: <VpnKeyOutlinedIcon />,
        roles: [ADMIN, USER_MANAGER],
        matches: ['/licenses', '/licenses/onechanger'],
      },
      {
        label: 'Bulk Activation',
        path: '/license-requests',
        icon: <AssignmentTurnedInOutlinedIcon />,
        roles: [ADMIN],
      },
    ],
  },
  {
    heading: 'Thanh toán',
    items: [
      {
        label: 'Cấu hình thanh toán',
        path: '/payments',
        icon: <PaymentsOutlinedIcon />,
        roles: [ADMIN],
      },
      {
        label: 'Bot Process',
        path: '/bot-process',
        icon: <PaymentsOutlinedIcon />,
        roles: [ADMIN],
      },
      {
        label: 'Bot Users',
        path: '/bot-users',
        icon: <PaymentsOutlinedIcon />,
        roles: [ADMIN],
      },
      {
        label: 'Bot Jobs',
        path: '/bot-jobs',
        icon: <PaymentsOutlinedIcon />,
        roles: [ADMIN],
      },
    ],
  },
  {
    heading: 'Config',
    items: [
      {
        label: 'API Keys',
        path: '/api-keys',
        icon: <VpnKeyOutlinedIcon />,
        roles: [ADMIN],
      },
      {
        label: 'Devices',
        path: '/devices',
        icon: <DevicesOtherOutlinedIcon />,
        roles: [ADMIN],
      },
      {
        label: 'Build Props',
        path: '/props',
        icon: <TerminalOutlinedIcon />,
        roles: [ADMIN],
      },
      {
        label: 'Roles',
        path: '/roles',
        icon: <AdminPanelSettingsOutlinedIcon />,
        roles: [ADMIN],
      },
      {
        label: 'Keyboxes',
        path: '/keyboxes',
        icon: <ShieldOutlinedIcon />,
        roles: [ADMIN],
      },
    ],
  },
];

/** Drop items the signed-in user cannot reach, then drop sections left empty. */
export const visibleSections = (groups: string[]): NavSection[] =>
  NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => item.roles.some((role) => groups.includes(role))),
  })).filter((section) => section.items.length > 0);

/** Longest-prefix match so `/licenses/onechanger` still lights up "Licenses". */
export const activeItem = (pathname: string, groups: string[]): NavItem | undefined => {
  const items = visibleSections(groups).flatMap((section) => section.items);
  const candidates = items.filter((item) =>
    [item.path, ...(item.matches ?? [])].some(
      (path) => pathname === path || pathname.startsWith(path + '/'),
    ),
  );
  return candidates.sort((a, b) => b.path.length - a.path.length)[0];
};
